import { useEffect, useState } from "react";
import axios from "axios";
import { Banknote, CreditCard, Download, Printer, Receipt, Smartphone, XCircle } from "lucide-react";
import { API, describeChoice, fmtErr, money } from "../lib/pos";
import { printBill } from "../lib/print";
import { useAuth } from "../context/AuthContext";
import { DateRangeField } from "../components/DateField";

export function CompletedOrders({ stations, billSettings }) {
  const { user: me } = useAuth();
  const isManager = me?.role === "manager";
  const today = () => new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(today());
  const [end, setEnd] = useState(today());
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // full order for pop-up
  const [changing, setChanging] = useState(null); // order for payment-mode change
  const [cancelling, setCancelling] = useState(null); // order to cancel
  const [cancelReason, setCancelReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      if (search.trim()) params.set("q", search.trim());
      const r = await axios.get(`${API}/orders/paid?${params.toString()}`);
      setRows(r.data);
    } catch (e) { alert(fmtErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [start, end]);

  const applyPreset = (which) => {
    const t = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (which === "today") { setStart(iso(t)); setEnd(iso(t)); }
    else if (which === "yesterday") { const y = new Date(t); y.setDate(t.getDate() - 1); setStart(iso(y)); setEnd(iso(y)); }
    else if (which === "7d") { const s = new Date(t); s.setDate(t.getDate() - 6); setStart(iso(s)); setEnd(iso(t)); }
    else if (which === "30d") { const s = new Date(t); s.setDate(t.getDate() - 29); setStart(iso(s)); setEnd(iso(t)); }
  };

  const changePaymentMethod = async (order, method) => {
    try {
      const r = await axios.patch(`${API}/orders/${order.id}/payment-method`, { method });
      setChanging(null);
      // update row in-place
      setRows((prev) => prev.map((x) => (x.id === order.id ? { ...x, ...r.data } : x)));
    } catch (e) { alert(fmtErr(e)); }
  };

  const cancelOrder = async () => {
    if (!cancelling) return;
    if (!cancelReason.trim()) { alert("Please enter a reason to cancel this order."); return; }
    try {
      await axios.post(`${API}/orders/${cancelling.id}/cancel`, { reason: cancelReason.trim() });
      setCancelling(null); setCancelReason("");
      load();
    } catch (e) { alert(fmtErr(e)); }
  };

  const fmtTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  // Local search on top of server search (so typing filters instantly)
  const term = search.trim().toLowerCase();
  const filtered = !term ? rows : rows.filter((o) =>
    (o.id || "").toLowerCase().includes(term) ||
    String(o.order_number || "").includes(term) ||
    (o.table || "").toLowerCase().includes(term) ||
    (o.billed_by_name || "").toLowerCase().includes(term)
  );

  return (
    <div className="workspace">
      <div className="page-head">
        <div>
          <p className="eyebrow">FRONT OFFICE / COMPLETED</p>
          <h1>Completed &amp; paid orders</h1>
          <p className="muted">Every paid, served or void bill for the selected dates. Click a row to see full detail or change payment mode{isManager ? " · managers can cancel within 48 hours" : ""}.</p>
        </div>
        {isManager && (
          <button
            data-testid="export-sales-xlsx-button"
            className="primary"
            onClick={() => {
              const url = new URL(`${API}/orders/paid/export.xlsx`);
              if (start) url.searchParams.set("start", start);
              if (end) url.searchParams.set("end", end);
              if (search.trim()) url.searchParams.set("q", search.trim());
              // Trigger auth-cookie carrying download in a new tab
              window.location.href = url.toString();
            }}
          ><Download size={14} style={{ marginRight: 6 }} />Export sales (XLSX)</button>
        )}
      </div>

      <div className="panel" data-testid="paid-orders-filters">
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div className="menu-search" style={{ flex: 1, minWidth: 240 }}>
            <input
              data-testid="paid-search-input"
              type="search"
              placeholder="Search by order number, ID, table or cashier…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && <button data-testid="paid-search-clear" className="search-clear" onClick={() => setSearch("")}>×</button>}
          </div>
          <label className="field" style={{ marginBottom: 0 }}>Range<DateRangeField start={start} end={end} onChange={({ start: s, end: e }) => { setStart(s); setEnd(e); }} testPrefix="paid" /></label>
          <div style={{ display: "flex", gap: 6 }}>
            <button data-testid="preset-today" className="outline" onClick={() => applyPreset("today")}>Today</button>
            <button data-testid="preset-yesterday" className="outline" onClick={() => applyPreset("yesterday")}>Yesterday</button>
            <button data-testid="preset-7d" className="outline" onClick={() => applyPreset("7d")}>Last 7d</button>
            <button data-testid="preset-30d" className="outline" onClick={() => applyPreset("30d")}>Last 30d</button>
          </div>
        </div>
      </div>

      {loading ? <p className="muted" style={{ marginTop: 14 }}>Loading…</p> : (
        <div className="menu-table paid-table" style={{ marginTop: 14 }}>
          <div className="table-head">
            <span>ORDER</span><span>TIME</span><span>TABLE / TAKEAWAY</span><span>CASHIER</span><span>PAYMENT</span><span>TOTAL</span><span>ACTIONS</span>
          </div>
          {filtered.length === 0 && (
            <div className="empty" style={{ minHeight: 160 }}>
              <Receipt size={26} />
              <b>No orders in range</b>
              <span>Pick a different date or clear the search</span>
            </div>
          )}
          {filtered.map((o) => {
            const isCancelled = o.status === "cancelled";
            return (
              <div className={`menu-line ${isCancelled ? "row-void" : ""}`} key={o.id} data-testid={`paid-row-${o.id}`}>
                <div>
                  <button data-testid={`open-order-${o.id}`} className="link-btn" onClick={() => setDetail(o)}>
                    #{String(o.order_number || "").padStart(4, "0")}
                  </button>
                  <small>{o.id.slice(-8)}</small>
                </div>
                <span>{fmtTime(o.paid_at || o.cancelled_at || o.created_at)}</span>
                <span>{o.table ? `Table ${o.table}` : (o.order_type === "table" ? "Dine-in" : "Takeaway")}</span>
                <span style={{ fontSize: 11 }}>{o.billed_by_name || o.created_by_name || "—"}</span>
                <span>
                  <b className={`method-pill method-${o.payment_method || "n"}`}>{(o.payment_method || "—").toUpperCase()}</b>
                  {isCancelled && <em style={{ display: "block", color: "#ff8080", fontSize: 10, marginTop: 2, fontStyle: "normal" }}>VOID</em>}
                </span>
                <strong>{money(o.total)}</strong>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <button
                    data-testid={`change-method-${o.id}`}
                    className="outline"
                    onClick={() => setChanging(o)}
                    disabled={isCancelled}
                    title={isCancelled ? "Cannot edit a cancelled order" : "Change payment method"}
                  >Change mode</button>
                  {isManager && (
                    <button
                      data-testid={`cancel-order-${o.id}`}
                      className="outline"
                      onClick={() => { setCancelling(o); setCancelReason(""); }}
                      disabled={!o.can_cancel || isCancelled}
                      title={isCancelled ? "Already cancelled" : (!o.can_cancel ? "Cancellation locked (>48h old)" : "Cancel this order")}
                    >{isCancelled ? "Cancelled" : "Cancel"}</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* --- Detail modal --- */}
      {detail && (
        <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && setDetail(null)}>
          <div className="modal" style={{ width: "min(540px, 94vw)", maxHeight: "88vh", overflowY: "auto" }} data-testid="order-detail-modal">
            <div className="section-line">
              <h2>Order #{String(detail.order_number || "").padStart(4, "0")}</h2>
              <button data-testid="close-detail-modal" onClick={() => setDetail(null)}>×</button>
            </div>
            <p className="muted" style={{ fontSize: 12, margin: "0 0 10px" }}>
              {detail.table ? `Table ${detail.table}` : (detail.order_type === "table" ? "Dine-in" : "Takeaway")} · Token T{String(detail.token_number || "").padStart(2, "0")} · {fmtTime(detail.paid_at || detail.created_at)}
            </p>
            <div className="row-kv"><span>Cashier</span><b>{detail.billed_by_name || detail.created_by_name || "—"}</b></div>
            <div className="row-kv"><span>Payment</span><b>{(detail.payment_method || "—").toUpperCase()}</b></div>
            <div className="row-kv"><span>Status</span><b>{detail.status.toUpperCase()}</b></div>
            <hr />
            <ul className="detail-items" data-testid="detail-items">
              {(detail.items || []).map((i, idx) => {
                const eff = Math.max(0, (i.quantity || 0) - (i.cancelled_quantity || 0));
                const desc = describeChoice(i.variant, i.addons);
                return (
                  <li key={idx} className={eff === 0 ? "line-void" : ""}>
                    <span>
                      <b>{eff || i.quantity}× {i.name}</b>
                      {desc && <em> ({desc})</em>}
                      {i.note && <div className="note-line">✎ {i.note}</div>}
                    </span>
                    <strong>{money(Number(i.price) * (eff || i.quantity))}</strong>
                  </li>
                );
              })}
            </ul>
            <hr />
            <div className="row-kv"><span>Subtotal</span><b>{money(detail.subtotal)}</b></div>
            {detail.discount > 0 && <div className="row-kv"><span>Discount{detail.discount_reason ? ` · ${detail.discount_reason}` : ""}</span><b>-{money(detail.discount)}</b></div>}
            <div className="row-kv"><span>GST</span><b>{money((detail.cgst || 0) + (detail.sgst || 0) + (detail.igst || 0))}</b></div>
            <div className="row-kv" style={{ fontSize: 15, fontWeight: 700, marginTop: 8 }}><span>TOTAL</span><b>{money(detail.total)}</b></div>
            <button
              data-testid="detail-reprint-bill"
              className="primary wide"
              style={{ marginTop: 14 }}
              onClick={() => printBill(detail, stations || [], { settings: billSettings, isDuplicate: true })}
            >
              <Printer size={14} style={{ marginRight: 6 }} />Reprint bill (duplicate)
            </button>
          </div>
        </div>
      )}

      {/* --- Change payment method --- */}
      {changing && (
        <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && setChanging(null)}>
          <div className="modal" style={{ width: "min(400px, 92vw)" }} data-testid="change-method-modal">
            <div className="section-line"><h2>Change payment method</h2><button onClick={() => setChanging(null)}>×</button></div>
            <p className="muted" style={{ fontSize: 12, margin: "0 0 12px" }}>Order #{String(changing.order_number || "").padStart(4, "0")} · Currently <b style={{ color: "#fff" }}>{(changing.payment_method || "—").toUpperCase()}</b></p>
            <div className="pay-row">
              <button data-testid="method-cash" className="pay-btn" onClick={() => changePaymentMethod(changing, "cash")}><Banknote size={16} />Cash</button>
              <button data-testid="method-card" className="pay-btn" onClick={() => changePaymentMethod(changing, "card")}><CreditCard size={16} />Card</button>
              <button data-testid="method-upi" className="pay-btn" onClick={() => changePaymentMethod(changing, "upi")}><Smartphone size={16} />UPI</button>
            </div>
          </div>
        </div>
      )}

      {/* --- Cancel order --- */}
      {cancelling && (
        <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && setCancelling(null)}>
          <div className="modal" style={{ width: "min(440px, 92vw)" }} data-testid="cancel-order-modal">
            <div className="section-line"><h2>Cancel order #{String(cancelling.order_number || "").padStart(4, "0")}</h2><button onClick={() => setCancelling(null)}>×</button></div>
            <p className="muted" style={{ fontSize: 12, margin: "0 0 10px" }}>
              This voids the bill for {money(cancelling.total)}. Cancellations are locked after 48 hours.
            </p>
            <label className="field">Reason
              <input data-testid="cancel-reason-input" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g. wrong bill / customer refund" />
            </label>
            <button data-testid="confirm-cancel-button" className="primary wide danger" onClick={cancelOrder} style={{ background: "#c73c39" }}>
              <XCircle size={14} style={{ marginRight: 6 }} />Confirm cancellation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ---------- Bill Settings (manager-only) ---------- */
/* ---------- Refund ledger (manager-only) ---------- */
